<template>
  <b-container
    class="commutator-general-tab"
    fluid
  >
    <b-row class="commutator-general-tab__gauge-visualization mx-0 mt-0">
      <b-col cols="12">
        <b-row class="mt-3">
          <b-col
            cols="auto"
            align-self="center"
          >
            <strong>
              {{ $t('general.select-visualization') }}
            </strong>
          </b-col>
          <b-col>
            <s-enum-select
              class="commutator-general-tab__dropdown"
              v-model="selectedVisualization"
              no-sort
              no-select-option
              translation-path="enums.engineering.commutator-temperature-differential"
              :enum-definition="visualizations"
              :disabled="!tabInfo.commutatorsAmount"
              @input="getCommutatorReadings($event, true)"
            />
          </b-col>
        </b-row>
        <div class="commutator-general-tab__legend">
          <div>
            <span class="commutator-general-tab__maximum-value-legend" />
            <span class="commutator-general-tab__legend-label">
              {{ $t('engineering.commutator-temperature-differential.maximum-value') }}
            </span>
          </div>
          <div>
            <span class="commutator-general-tab__current-value-legend" />
            <span class="commutator-general-tab__legend-label">
              {{ $t('engineering.commutator-temperature-differential.current-value') }}
            </span>
          </div>
        </div>
        <div
          v-show="!loadingReadings"
          class="commutator-general-tab__gauges avoid-break"
          v-b-tooltip="$t('messages.MI026.message')"
        >
          <commutator-gauge
            v-for="(item, index) in readings"
            :key="item.commutatorId"
            :reading="item"
            :min-max-values="minMaxGauge"
            :is-transformer="isTransformer"
            :loading-reset="loadingReset[index]"
            @reset="reset($event, index)"
          />
          <s-empty-list v-show="!readings.length" />
        </div>
        <s-loader
          v-show="loadingReadings"
          class="h-75"
          size="lg"
        />
      </b-col>
    </b-row>
    <b-row
      v-show="loadingDetails"
      class="commutator-general-tab__loader-border m-0 py-5"
    >
      <b-col cols="12">
        <s-loader
          class="h-75"
          size="lg"
        />
      </b-col>
    </b-row>
    <b-row
      v-show="!loadingDetails"
      class="m-0 commutator-general-tab__accordion-container"
    >
      <accordion-panel
        v-for="(commutator, index) in commutators"
        class="avoid-break"
        :key="index"
        :collapsed.sync="commutator.collapsed"
        :id="index"
      >
        <b-row slot="title">
          <strong class="ml-3">
            {{ $t('engineering.commutator-temperature-differential.title') }}
            {{ commutator.commutatorId }}
          </strong>
        </b-row>
        <b-row class="commutator-general-tab__cards">
          <engineering-card
            v-for="(detail, index) in commutator.details"
            :key="`${detail.name}-${index}`"
            :detail="detail"
          />
        </b-row>
        <s-empty-list
          v-if="!commutator.details.length"
          class="py-5"
        />
      </accordion-panel>
    </b-row>
  </b-container>
</template>

<script>
import { CommutatorVisualizations } from 'sigma-mappings';
import CommutatorGauge from '@/domain/components/installation/engineering/commutatorTemperatureDifferential/CommutatorGauge';
import Service from '@/domain/services/installation/engineering/commutatorTemperatureDifferential/commutatorTemperatureDifferentialService';
import eventBus from '@/services/globalEventBus';
import engineeringEvents from '@/domain/events/engineering';
import AccordionPanel from '@/components/AccordionPanel';
import EngineeringCard from '../EngineeringCard';

export default {
  name: 'CommutatorTemperatureDifferentialTab',
  props: {
    assetModule: {
      type: Object,
      default: () => ({}),
    },
    tabInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    CommutatorGauge,
    AccordionPanel,
    EngineeringCard,
  },
  data: () => ({
    readings: [],
    commutators: {},
    visualizations: CommutatorVisualizations,
    selectedVisualization: CommutatorVisualizations.INSTANT_COMMUTATOR_OIL_TEMPERATURE_DIFFERENTIAL,
    loadingReadings: true,
    loadingDetails: true,
    loadingReset: {
      0: false,
      1: false,
      2: false,
    },
  }),
  computed: {
    isTransformer() {
      return this.selectedVisualization === CommutatorVisualizations.TRANSFORMER_OIL_TEMPERATURE;
    },
    minMaxGauge() {
      const firstRange = { min: 0, max: 200 };
      const secondRange = { min: -50, max: 50 };
      const minMaxByVisualization = {
        [CommutatorVisualizations.COMMUTATOR_OIL_TEMPERATURE]: firstRange,
        [CommutatorVisualizations.TRANSFORMER_OIL_TEMPERATURE]: firstRange,
        [CommutatorVisualizations.INSTANT_COMMUTATOR_OIL_TEMPERATURE_DIFFERENTIAL]: secondRange,
        [CommutatorVisualizations.FILTERED_COMMUTATOR_OIL_TEMPERATURE_DIFFERENTIAL]: secondRange,
      };
      return minMaxByVisualization[this.selectedVisualization];
    },
  },
  created() {
    this.init();
    this.refreshData(true);
  },
  beforeDestroy() {
    this.deinit();
  },
  methods: {
    reset(commutatorId, gaugeIndex) {
      const payload = {
        commutatorId,
        visualization: this.selectedVisualization,
      };
      this.loadingReset[gaugeIndex] = true;
      Service.patchCommutatorDetails(this.$route.params.assetModuleId, payload).finally(() => {
        this.getCommutatorReadings().finally(() => {
          this.loadingReset[gaugeIndex] = false;
        });
      });
    },
    refreshData(showLoading) {
      if (!this.tabInfo.commutatorsAmount) {
        return null;
      }
      return Promise.allSettled([
        this.getCommutatorReadings(this.selectedVisualization, showLoading),
        this.getCommutatorDetails(showLoading),
      ]);
    },
    sortReadings() {
      this.readings.sort(
        (first, second) => first.commutatorId - second.commutatorId,
      );
    },
    getCommutatorReadings(visualization = this.selectedVisualization, showLoading = false) {
      const { id } = this.assetModule;
      this.loadingReadings = showLoading;
      return Service.getCommutatorReadings(id, visualization)
        .then((response) => {
          this.readings = response;
          this.sortReadings();
        })
        .finally(() => {
          this.loadingReadings = false;
        });
    },
    sortCommutatorDetails(index) {
      const commutator = this.commutators[index] || {};
      commutator.details.sort((first, second) => first.name.localeCompare(second.name));
    },
    getCommutatorDetails(showLoading = false) {
      const { commutatorsAmount } = this.tabInfo;
      this.loadingDetails = showLoading;
      const promises = [];

      for (let commutatorId = 1; commutatorId <= commutatorsAmount; commutatorId += 1) {
        promises.push(this.getCommutatorDetail(commutatorId));
      }
      return Promise.allSettled(promises)
        .then((response) => {
          response.forEach((item, index) => {
            this.$set(this.commutators, index + 1, item.value);
            this.sortCommutatorDetails(index + 1);
          });
        }).finally(() => {
          this.loadingDetails = false;
        });
    },
    getCommutatorDetail(commutatorId) {
      const { id } = this.assetModule;
      return Service.getCommutatorDetails(id, commutatorId);
    },
    init() {
      eventBus.on(engineeringEvents.refreshDataTab, this.refreshDataEvent, this);
    },
    deinit() {
      eventBus.off(engineeringEvents.refreshDataTab, this.refreshDataEvent, this);
    },
    refreshDataEvent(showLoading = false) {
      this.refreshData(showLoading);
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"

  .commutator-general-tab
    padding: 10px 0
    &__details
      padding: 15px
    &__loader-border
      border: 1px solid var(--color-border-dark)
      border-radius: 5px
      width: 100%
    &__gauge-visualization
      border: 1px solid var(--color-border-dark)
      border-radius: 5px
      min-height: 50vh
      width: 100%
      margin-bottom: 15px
      +media-min-xl
        min-height: 40vh
    &__dropdown ::v-deep .dropdown-toggle
      min-width: 10rem !important
    &__gauges
      display: flex
      justify-content: center
      flex-wrap: wrap
      margin: 0 -15px
    &__accordion-container
      max-height: 450px
      overflow-y: auto
    &__maximum-value-legend
      height: 15px
      width: 15px
      background-color: var(--color-danger-dark)
      display: inline-block
    &__current-value-legend
      height: 15px
      width: 15px
      background-color: var(--color-close)
      display: inline-block
      +media-min-md
        margin-left: 15px
    &__legend
      margin-top: 15px
      text-align: left
      display: flex
      flex-wrap: wrap
      +media-min-sm
        gap: 65px
    &__legend-label
      vertical-align: text-bottom
      margin-left: 5px
    &__cards
      display: grid
      gap: 18px
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr))
      +media-min-sm
        margin: 0 10px
    ::v-deep
      .accordion-panel
        margin-top: 5px
        box-shadow: none
        &__header
          padding: 3px 15px !important
  @media print
    .commutator-general-tab
      page-break-after: always
      &__accordion-container
        max-height: unset
      &__gauge-visualization
        min-height: 30vh
      &__accordion-container
        display: block
</style>
